import { API_URL } from "../config/api";

const LIBRARY_BOOK_ALREADY_PRESENT = "Ce livre est déjà présent dans votre bibliothèque";
const PLAYER_BOOK_MARK = "mine~";

function safeEpubName(raw) {
  const base = String(raw || "").split(/[/\\]/).pop() || "";
  const cleaned = base
    .replace(/\.epub$/i, "")
    .replace(/[^\w.\- ()'\u00C0-\u024F]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  if (!cleaned || cleaned === "." || cleaned.includes("..")) return "";
  return `${cleaned}.epub`;
}

function alreadyPresentError() {
  const error = new Error(LIBRARY_BOOK_ALREADY_PRESENT);
  error.duplicate = true;
  return error;
}

async function readJson(res) {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || "Impossible de lire la bibliothèque.");
  }
  return json;
}

export function fetchLibraryBooks() {
  return fetch(`${API_URL}/library/books`, { credentials: "include" }).then(readJson);
}

export function fetchLibraryBook(bookId) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}`, {
    credentials: "include",
  }).then(readJson);
}

export function fetchLibraryChapter(bookId, index) {
  return fetch(
    `${API_URL}/library/books/${encodeURIComponent(bookId)}/chapters/${index}`,
    { credentials: "include" }
  ).then(readJson);
}

async function fetchChaptersOneByOne(bookId) {
  const book = await fetchLibraryBook(bookId);
  const count = book.chapters?.length || 0;
  const chapters = new Array(count);
  let next = 0;
  const worker = async () => {
    while (next < count) {
      const index = next;
      next += 1;
      chapters[index] = await fetchLibraryChapter(bookId, index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, count) }, () => worker()));
  return { chapters };
}

export async function fetchLibraryReading(bookId) {
  const res = await fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/reading`, {
    credentials: "include",
  });
  if (res.ok) {
    const json = await res.json().catch(() => ({}));
    if (Array.isArray(json.chapters)) return json;
  }
  const json = await res.json().catch(() => ({}));
  if (res.status === 404 && !json.message) return fetchChaptersOneByOne(bookId);
  throw new Error(json.message || "Impossible de lire le livre.");
}

export async function deleteLibraryBook(bookId) {
  const res = await fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (res.status === 204) return { ok: true };
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || "Impossible de retirer le livre.");
  return json;
}

export function fetchLibraryHighlights(bookId) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/highlights`, {
    credentials: "include",
  }).then(readJson);
}

export function fetchLibraryHighlightCounts() {
  return fetch(`${API_URL}/library/highlights`, { credentials: "include" }).then(readJson);
}

export function createLibraryHighlight(bookId, body) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/highlights`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible d'enregistrer le surlignage.");
    return json;
  });
}

export function fetchLibraryCharacterCatalog() {
  return fetch(`${API_URL}/library/character-books`, { credentials: "include" }).then(readJson);
}

export function fetchLibraryCharacters(bookId) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/characters`, {
    credentials: "include",
  }).then(readJson);
}

export function createLibraryCharacter(bookId, body) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/characters`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible d'enregistrer le personnage.");
    return json;
  });
}

export function updateLibraryCharacter(bookId, characterId, body) {
  return fetch(
    `${API_URL}/library/books/${encodeURIComponent(bookId)}/characters/${encodeURIComponent(characterId)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  ).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible de modifier le personnage.");
    return json;
  });
}

export function deleteLibraryCharacter(bookId, characterId) {
  return fetch(
    `${API_URL}/library/books/${encodeURIComponent(bookId)}/characters/${encodeURIComponent(characterId)}`,
    { method: "DELETE", credentials: "include" }
  ).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible de supprimer le personnage.");
    return json;
  });
}

export function unassignLibraryPassage(bookId, highlightId) {
  return fetch(
    `${API_URL}/library/books/${encodeURIComponent(bookId)}/characters/passages/${encodeURIComponent(highlightId)}`,
    { method: "DELETE", credentials: "include" }
  ).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible de retirer le lien.");
    return json;
  });
}

export function assignLibraryPassage(bookId, body) {
  return fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/characters/assign`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible d'associer le passage.");
    return json;
  });
}

export function deleteLibraryHighlight(bookId, highlightId) {
  return fetch(
    `${API_URL}/library/books/${encodeURIComponent(bookId)}/highlights/${encodeURIComponent(highlightId)}`,
    { method: "DELETE", credentials: "include" }
  ).then(async (res) => {
    if (res.status === 204) return { ok: true };
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || "Impossible de retirer le surlignage.");
    return json;
  });
}

export async function uploadLibraryBook(file) {
  const storedName = safeEpubName(file?.name || "");
  if (storedName) {
    try {
      const listed = await fetchLibraryBooks();
      const id = `${PLAYER_BOOK_MARK}${storedName}`;
      if ((listed.books || []).some((book) => book.id === id)) {
        throw alreadyPresentError();
      }
    } catch (err) {
      if (err.duplicate) throw err;
    }
  }
  const name = encodeURIComponent(file?.name || "livre.epub");
  const res = await fetch(`${API_URL}/library/books?name=${name}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/epub+zip" },
    body: file,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (json.message === LIBRARY_BOOK_ALREADY_PRESENT) throw alreadyPresentError();
    throw new Error(json.message || "Impossible d'enregistrer le livre.");
  }
  return json;
}

export function libraryCoverUrl(bookId) {
  return `${API_URL}/library/books/${encodeURIComponent(bookId)}/cover`;
}
